import { escapeReferenceToken } from '@criteria/json-pointer'
import {
  BreadcrumbItem,
  BreadcrumbLabel,
  BreadcrumbList,
  BreadcrumbSeparator,
  Button,
  ErrorMessage,
  Heading,
  SchemaDrivenFieldset
} from '@criteria-labs/react'
import { Service } from '@criteria-labs/docs-kit'
import { XMarkIcon } from '@heroicons/react/24/solid'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { PageFormBody, PageFormFooter } from '../PageForm'
import { InspectorArticle, InspectorBody, InspectorHeader } from '../article'
import { AddSecurityScheme, SecurityScheme, SecuritySchemeRef } from '../changes/security-scheme'
import { useRouter } from '../router'
import { toUniqueProposedName } from '../toUniqueProposedName'
import { useDispatchChange } from '../useDispatchChange'
import { messsageFromError } from '../util/messageFromError'

type Inputs = {
  securitySchemeName: string
  in: string
  name: string
  description?: string
}

export function NewAPIKeyAuth({ service }: { service: Service }) {
  const { push } = useRouter()

  const defaultIdentifier = toUniqueProposedName(
    'APIKeyAuth',
    Object.keys(service.openAPI.components?.securitySchemes ?? {})
  )

  const dispatchChange = useDispatchChange()
  const {
    handleSubmit,
    formState: { errors, ...formState },
    setError,
    ...methods
  } = useForm<Inputs>({
    defaultValues: {
      securitySchemeName: defaultIdentifier,
      in: 'header'
    }
  })

  const onSubmit: SubmitHandler<Inputs> = ({ securitySchemeName, in: location, name, description }) => {
    try {
      const ref: SecuritySchemeRef = `#/components/securitySchemes/${escapeReferenceToken(securitySchemeName)}`
      const value: SecurityScheme = {
        type: 'apiKey',
        in: location as 'query' | 'header' | 'cookie',
        name,
        description: description && description.length > 0 ? description : undefined
      }
      dispatchChange(new AddSecurityScheme(ref, value))
      push('/')
    } catch (error) {
      setError('root', { message: messsageFromError(error) ?? 'Could not create security scheme' })
    }
  }

  return (
    <InspectorArticle>
      <InspectorHeader className="space-y-3">
        <div className="flex items-center justify-between">
          <BreadcrumbList className="min-h-10">
            <BreadcrumbItem onClick={() => push('/')}>
              <BreadcrumbLabel>API</BreadcrumbLabel>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLabel>New API Key</BreadcrumbLabel>
            </BreadcrumbItem>
          </BreadcrumbList>
          <Button aria-label="Close" plain onClick={() => push('/')}>
            <XMarkIcon />
          </Button>
        </div>
        <Heading>Create a new API key</Heading>
      </InspectorHeader>
      <InspectorBody>
        <FormProvider {...{ handleSubmit, formState: { errors, ...formState }, setError, ...methods }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            {errors.root && <ErrorMessage className="mt-3">{errors.root.message}</ErrorMessage>}
            <PageFormBody className="space-y-8">
              <SchemaDrivenFieldset
                schema={{
                  type: 'object',
                  required: ['securitySchemeName', 'in', 'name'],
                  properties: {
                    securitySchemeName: {
                      title: 'Security scheme name',
                      description: 'A key that will identify the security scheme object in the OpenAPI document.',
                      type: 'string'
                    },
                    name: {
                      title: 'API key name',
                      description: 'The name of the API key field in HTTP requests.',
                      type: 'string',
                      minLength: 1
                    },
                    in: {
                      title: 'API key location',
                      type: 'string',
                      enum: ['query', 'header', 'cookie'],
                      'x-enumTitles': ['Query', 'Header', 'Cookie'],
                      description: 'Where the API key appears in HTTP requests.'
                    },
                    description: {
                      title: 'Description',
                      type: 'string',
                      format: 'multiline'
                    }
                  }
                }}
              />
            </PageFormBody>
            <PageFormFooter>
              <Button type="submit" color="indigo">
                Create API Key
              </Button>
            </PageFormFooter>
          </form>
        </FormProvider>
      </InspectorBody>
    </InspectorArticle>
  )
}
